import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import useI18n from '../../i18n/useI18n'
import Modal from '../../components/modal'
import { BadgeCellRenderer } from '../../components/table-cell-renderers'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'

const AgentDetailModal = ({ isOpen, agent, onClose }) => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const [details, setDetails] = useState(null)
  const [contractStatus, setContractStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingContractStatus, setLoadingContractStatus] = useState(false)

  const activeAgent = details || agent
  const user = activeAgent?.user || {}

  useEffect(() => {
    if (isOpen && agent?.id) {
      setLoading(true)
      setLoadingContractStatus(true)
      
      // Fetch agent details
      dispatch(
        ApiCallActions.Get({
          url: `agents/${agent.id}`,
          showAlertOnError: true,
          showLoading: false,
          onSuccess: ({ data }) => {
            const payload = data?.data || data || null
            setDetails(payload)
            setLoading(false)
          },
          onError: ({ errorMessage }) => {
            if (errorMessage) {
              general.notificationError(errorMessage)
            }
            setDetails(null)
            setLoading(false)
          }
        })
      )

      // Fetch contract status
      dispatch(
        ApiCallActions.Get({
          url: `agents/${agent.id}/contract-status`,
          showAlertOnError: false,
          showLoading: false,
          onSuccess: ({ data }) => {
            const payload = data?.data || data || null
            setContractStatus(payload)
            setLoadingContractStatus(false)
          },
          onError: () => {
            setContractStatus(null)
            setLoadingContractStatus(false)
          }
        })
      )
    }

    if (!isOpen) {
      setDetails(null)
      setContractStatus(null)
    }
  }, [dispatch, isOpen, agent?.id])

  const getAgentName = useMemo(() => {
    if (!activeAgent) return t('AGENTS_ENTITY')
    if (activeAgent.companyName) return activeAgent.companyName
    if (activeAgent.name) return activeAgent.name
    if (activeAgent.fullName) return activeAgent.fullName
    const firstName = activeAgent?.user?.firstName || ''
    const lastName = activeAgent?.user?.lastName || ''
    const combined = `${firstName} ${lastName}`.trim()
    return combined || activeAgent?.user?.email || t('AGENTS_ENTITY')
  }, [activeAgent, t])

  const logoUrl = useMemo(() => {
    if (!activeAgent) return ''
    return activeAgent.logoUrl || activeAgent.logoURL || activeAgent.logo
  }, [activeAgent])

  const renderInfo = (label, value, icon) => {
    const displayValue =
      value === null || value === undefined || value === '' ? '-' : value

    return (
      <div className="m-b-15 d-flex">
        {icon && <i className={`zmdi ${icon} m-r-10 text-warning`} style={{ fontSize: '18px' }}></i>}
        <div>
          <small className="text-muted d-block">{label}</small>
          <strong>{displayValue}</strong>
        </div>
      </div>
    )
  }

  const renderLink = (label, url, icon, displayText = null) => {
    const hasUrl = typeof url === 'string' && url.trim()

    return (
      <div className="m-b-15 d-flex">
        {icon && <i className={`zmdi ${icon} m-r-10 text-warning`} style={{ fontSize: '18px' }}></i>}
        <div>
          <small className="text-muted d-block">{label}</small>
          {hasUrl ? (
            <a href={url} target="_blank" rel="noreferrer">
              {displayText || url}
            </a>
          ) : (
            <strong>-</strong>
          )}
        </div>
      </div>
    )
  }

  const getContractStatusLabel = () => {
    if (!contractStatus) return null
    
    const statusMap = {
      active: t('AGENTS_CONTRACT_STATUS_ACTIVE'),
      expired: t('AGENTS_CONTRACT_STATUS_EXPIRED'),
      no_contract: t('AGENTS_CONTRACT_STATUS_NO_CONTRACT')
    }
    
    return statusMap[contractStatus.status] || contractStatus.status
  }

  const getContractStatusClass = () => {
    if (!contractStatus) return 'badge-secondary'
    
    const statusClassMap = {
      active: 'badge-success',
      expired: 'badge-danger',
      no_contract: 'badge-warning'
    }
    
    return statusClassMap[contractStatus.status] || 'badge-secondary'
  }

  const renderBadgeCard = (icon, label, badge, color = '#ff9800') => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 14px',
        background: '#ffffff',
        border: `1px solid ${color}20`,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease',
        minWidth: 'fit-content'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)'
        e.currentTarget.style.transform = 'translateY(-1px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      <i
        className={`zmdi ${icon}`}
        style={{
          fontSize: '20px',
          color: color,
          minWidth: '20px'
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
        <small
          style={{
            fontSize: '11px',
            color: '#64748b',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            lineHeight: '1.2'
          }}
        >
          {label}
        </small>
        <div style={{ lineHeight: '1' }}>{badge}</div>
      </div>
    </div>
  )

  const hasActiveContractBadge = renderBadgeCard(
    'zmdi-file-text',
    t('AGENTS_DETAIL_BADGE_ACTIVE_CONTRACT'),
    <BadgeCellRenderer
      value={contractStatus?.hasActiveContract ?? activeAgent?.hasActiveContract}
      options={{
        trueLabel: t('AGENTS_TABLE_HAS_ACTIVE_CONTRACT_YES'),
        falseLabel: t('AGENTS_TABLE_HAS_ACTIVE_CONTRACT_NO'),
        trueClass: 'badge-success',
        falseClass: 'badge-danger'
      }}
    />,
    contractStatus?.hasActiveContract ?? activeAgent?.hasActiveContract ? '#28a745' : '#dc3545'
  )

  const contractStatusBadge = contractStatus?.status && renderBadgeCard(
    'zmdi-assignment-check',
    t('AGENTS_DETAIL_BADGE_CONTRACT_STATUS'),
    <span className={`badge ${getContractStatusClass()}`}>
      {getContractStatusLabel()}
    </span>,
    contractStatus.status === 'active' ? '#28a745' : contractStatus.status === 'expired' ? '#dc3545' : '#ffc107'
  )

  const contractExpiringSoonBadge = contractStatus?.contractExpiringSoon && renderBadgeCard(
    'zmdi-time-countdown',
    t('AGENTS_DETAIL_BADGE_CONTRACT_EXPIRING'),
    <span className="badge badge-warning">
      {t('AGENTS_CONTRACT_EXPIRING_SOON')}
    </span>,
    '#ff9800'
  )

  const statusBadge = renderBadgeCard(
    'zmdi-account-circle',
    t('AGENTS_DETAIL_BADGE_USER_STATUS'),
    <BadgeCellRenderer
      value={user?.status === 'active'}
      options={{
        trueLabel: t('AGENTS_TABLE_STATUS_ACTIVE'),
        falseLabel: t('AGENTS_TABLE_STATUS_INACTIVE'),
        trueClass: 'badge-success',
        falseClass: 'badge-danger'
      }}
    />,
    user?.status === 'active' ? '#28a745' : '#dc3545'
  )

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('AGENTS_DETAIL_TITLE')}
      subtitle={t('AGENTS_DETAIL_SUBTITLE')}
      icon="zmdi-account-box"
      size="large"
    >
      {loading ? (
        <div className="text-center p-4">
          <i className="zmdi zmdi-refresh zmdi-hc-spin" style={{ fontSize: '32px' }}></i>
          <p className="m-t-10">{t('AGENTS_DETAIL_LOADING')}</p>
        </div>
      ) : !activeAgent ? (
        <div className="text-center p-4">
          <p className="text-muted">{t('AGENT_NOT_FOUND')}</p>
        </div>
      ) : (
        <>
          <div className="m-b-20 d-flex align-items-center" style={{ gap: '12px' }}>
            {logoUrl && (
              <img
                src={logoUrl}
                alt={getAgentName}
                style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, background: '#f7f7f7' }}
              />
            )}
            <div style={{ flex: 1 }}>
              <h5 className="m-b-5" style={{ marginBottom: '12px', fontSize: '20px', fontWeight: '600', color: '#1e293b' }}>
                {getAgentName}
              </h5>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px',
                  alignItems: 'start'
                }}
              >
                {statusBadge}
                {hasActiveContractBadge}
                {contractStatusBadge}
                {contractExpiringSoonBadge}
              </div>
            </div>
          </div>

          <div className="row clearfix">
            <div className="col-md-6">
              <h6 className="m-b-15">{t('AGENTS_DETAIL_SECTIONS_BASIC_INFO')}</h6>
              {renderInfo(t('AGENTS_DETAIL_FIELDS_COMPANY_NAME'), activeAgent.companyName || getAgentName, 'zmdi-city')}
              {renderInfo(
                t('AGENTS_DETAIL_FIELDS_EMAIL'),
                activeAgent.email || user?.email,
                'zmdi-email'
              )}
              {renderInfo(
                t('AGENTS_DETAIL_FIELDS_PHONE'),
                activeAgent.phone || user?.phone,
                'zmdi-phone'
              )}
              {renderInfo(t('AGENTS_DETAIL_FIELDS_COUNTRY'), activeAgent.country, 'zmdi-globe')}
              {renderInfo(t('AGENTS_DETAIL_FIELDS_CITY'), activeAgent.city, 'zmdi-city')}
              {renderInfo(t('AGENTS_DETAIL_FIELDS_ADDRESS'), activeAgent.address, 'zmdi-pin')}
            </div>
            <div className="col-md-6">
              <h6 className="m-b-15">{t('AGENTS_DETAIL_SECTIONS_CONTRACT_INFO')}</h6>
              {loadingContractStatus ? (
                <div className="m-b-15">
                  <small className="text-muted">{t('AGENTS_CONTRACT_STATUS_LOADING')}</small>
                </div>
              ) : contractStatus ? (
                <>
                  {renderInfo(
                    t('AGENTS_DETAIL_FIELDS_CONTRACT_STATUS'),
                    getContractStatusLabel(),
                    'zmdi-file-text'
                  )}
                  {contractStatus.contractStartDate && renderInfo(
                    t('AGENTS_DETAIL_FIELDS_CONTRACT_START_DATE'),
                    general.formatDate(contractStatus.contractStartDate),
                    'zmdi-calendar'
                  )}
                  {contractStatus.contractEndDate && renderInfo(
                    t('AGENTS_DETAIL_FIELDS_CONTRACT_END_DATE'),
                    general.formatDate(contractStatus.contractEndDate),
                    'zmdi-calendar-check'
                  )}
                  {contractStatus.licenseNumber && renderInfo(
                    t('AGENTS_DETAIL_FIELDS_LICENSE_NUMBER'),
                    contractStatus.licenseNumber,
                    'zmdi-shield-check'
                  )}
                  {contractStatus.licenseExpiryDate && renderInfo(
                    t('AGENTS_DETAIL_FIELDS_LICENSE_EXPIRY_DATE'),
                    general.formatDate(contractStatus.licenseExpiryDate),
                    'zmdi-calendar-alt'
                  )}
                  {contractStatus.contractFile && renderLink(
                    t('AGENTS_DETAIL_FIELDS_CONTRACT_FILE'),
                    contractStatus.contractFile,
                    'zmdi-file',
                    t('AGENTS_DETAIL_VIEW_CONTRACT_FILE')
                  )}
                </>
              ) : (
                <>
                  {renderInfo(t('AGENTS_DETAIL_FIELDS_LICENSE_NUMBER'), activeAgent.licenseNumber, 'zmdi-shield-check')}
                  {renderInfo(
                    t('AGENTS_DETAIL_FIELDS_LICENSE_EXPIRY_DATE'),
                    general.formatDate(activeAgent.licenseExpiryDate),
                    'zmdi-calendar-alt'
                  )}
                  {renderInfo(
                    t('AGENTS_DETAIL_FIELDS_CONTRACT_START_DATE'),
                    general.formatDate(activeAgent.contractStartDate),
                    'zmdi-calendar'
                  )}
                  {renderInfo(
                    t('AGENTS_DETAIL_FIELDS_CONTRACT_END_DATE'),
                    general.formatDate(activeAgent.contractEndDate),
                    'zmdi-calendar-check'
                  )}
                  {activeAgent.contractFile && renderLink(
                    t('AGENTS_DETAIL_FIELDS_CONTRACT_FILE'),
                    activeAgent.contractFile,
                    'zmdi-file',
                    t('AGENTS_DETAIL_VIEW_CONTRACT_FILE')
                  )}
                </>
              )}
              {renderInfo(t('AGENTS_DETAIL_FIELDS_TAX_NUMBER'), activeAgent.taxNumber, 'zmdi-balance')}
              {renderInfo(t('AGENTS_DETAIL_FIELDS_TAX_OFFICE'), activeAgent.taxOffice, 'zmdi-city-alt')}
              {renderLink(t('AGENTS_DETAIL_FIELDS_WEBSITE'), activeAgent.websiteUrl || activeAgent.website, 'zmdi-globe-alt')}
            </div>
          </div>

          {activeAgent.description && (
            <div className="m-t-20">
              <h6 className="m-b-15">{t('AGENTS_DETAIL_SECTIONS_DESCRIPTION')}</h6>
              <p className="text-muted">{activeAgent.description}</p>
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

export default AgentDetailModal

